// src/api/brand/brandApi.ts
import { axiosInstance } from "src/api/axiosInstance";
import type { ApiResponse } from "src/api/common/apiTypes";

export type BrandFile = {
  fileId: number;
  originalName: string;
  savedName: string;
  fileUrl: string;
  fileSize: number;
  contentType: string;
  fileOrder: number;
};
export type BrandListItem = {
  id: number;

  brandCode?: string;
  brandCategoryId?: number;
  brandCategoryName?: string;

  brandNameKo: string;
  brandNameEn?: string;
  brandDescription?: string;

  brandLogoFileGrpId?: number;
  logoFiles?: BrandFile[]; // ✅ 추가

  brandThumbnailFileGrpId?: number;
  thumbnailFiles?: BrandFile[]; // ✅ 추가

  officialWebsite?: string;
  originCountry?: string;

  displayOrder?: number;
  isFeatured?: boolean;
  isActive?: boolean;

  createdAt?: string;
  updatedAt?: string;
};

export type BrandItem = BrandListItem & {
  // detail에 더 오는 게 있으면 여기에 추가
};

export type BrandListResponse = {
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
  size: number;
  content: BrandListItem[];
  number: number; // 0-based
  numberOfElements: number;
  empty: boolean;
  sort?: any;
  pageable?: any;
};

export type GetBrandListParams = {
  brandName?: string;
  categoryId?: number;
  isActive?: boolean;
  isFeatured?: boolean;
  page: number; // 0-based
  size: number;
  order: string; // "displayOrder,asc"
};

export type BrandUpsertBody = {
  brandCode?: string;
  brandCategoryId?: number;

  brandNameKo: string;
  brandNameEn?: string;
  brandDescription?: string;

  officialWebsite?: string;
  originCountry?: string;

  displayOrder?: number;
  isFeatured?: boolean;
  isActive?: boolean;
};

export type BrandCategoryItem = {
  id: number;
  categoryCode: string;
  categoryName: string;
};

export const getBrandCategoryListApi = async (): Promise<ApiResponse<BrandCategoryItem[]>> => {
  const res = await axiosInstance.get<ApiResponse<BrandCategoryItem[]>>(
    "/v1/admin/baseinfo/brand/category/list"
  );
  return res.data;
};

function buildBrandMultipart(
  body: BrandUpsertBody,
  logoFile?: File | null,
  thumbnailFile?: File | null
): FormData {
  const fd = new FormData();
  fd.append("data", new Blob([JSON.stringify(body)], { type: "application/json" }));
  if (logoFile) fd.append("logoFile", logoFile);
  if (thumbnailFile) fd.append("thumbnailFile", thumbnailFile);
  return fd;
}

// LIST
export const getBrandListApi = async (
  params: GetBrandListParams
): Promise<ApiResponse<BrandListResponse>> => {
  const res = await axiosInstance.get<ApiResponse<BrandListResponse>>(
    "/v1/admin/baseinfo/brand/list",
    {
      params: {
        brandName: params.brandName || undefined,
        categoryId: params.categoryId ?? undefined,
        isActive: typeof params.isActive === "boolean" ? params.isActive : undefined,
        isFeatured: typeof params.isFeatured === "boolean" ? params.isFeatured : undefined,
        page: params.page,
        size: params.size,
        order: params.order,
      },
    }
  );
  return res.data;
};

// DETAIL
export const getBrandApi = async (id: number): Promise<ApiResponse<BrandItem>> => {
  const res = await axiosInstance.get<ApiResponse<BrandItem>>(`/v1/admin/baseinfo/brand/${id}`);
  return res.data;
};

// CREATE (multipart)
export const createBrandApi = async (
  payload: BrandUpsertBody,
  logoFile?: File | null,
  thumbnailFile?: File | null
): Promise<ApiResponse<BrandItem>> => {
  const fd = buildBrandMultipart(payload, logoFile, thumbnailFile);
  const res = await axiosInstance.post<ApiResponse<BrandItem>>("/v1/admin/baseinfo/brand", fd);
  return res.data;
};

// UPDATE (multipart)
export const updateBrandApi = async (
  id: number,
  payload: BrandUpsertBody,
  logoFile?: File | null,
  thumbnailFile?: File | null
): Promise<ApiResponse<BrandItem>> => {
  const fd = buildBrandMultipart(payload, logoFile, thumbnailFile);
  const res = await axiosInstance.put<ApiResponse<BrandItem>>(`/v1/admin/baseinfo/brand/${id}`, fd);
  return res.data;
};

// DELETE
export const deleteBrandApi = async (id: number): Promise<ApiResponse<null>> => {
  const res = await axiosInstance.delete<ApiResponse<null>>(`/v1/admin/baseinfo/brand/${id}`);
  return res.data;
};

/** ✅ 업로드(별도) - swagger에서 { file: binary } 형태면 fieldName은 "file"이 맞음 */
function buildSingleFileMultipart(file: File): FormData {
  const fd = new FormData();
  fd.append("file", file);
  return fd;
}

// 로고 업로드
export const uploadBrandLogoMultipartApi = async (
  id: number,
  file: File
): Promise<ApiResponse<any>> => {
  const fd = buildSingleFileMultipart(file);
  const res = await axiosInstance.post<ApiResponse<any>>(`/v1/admin/baseinfo/brand/${id}/logo`, fd);
  return res.data;
};

// 이미지 업로드
export const uploadBrandImageMultipartApi = async (
  id: number,
  file: File
): Promise<ApiResponse<any>> => {
  const fd = buildSingleFileMultipart(file);
  const res = await axiosInstance.post<ApiResponse<any>>(
    `/v1/admin/baseinfo/brand/${id}/image`,
    fd
  );
  return res.data;
};
